// src/pages/PlayerPage.js

import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';

// Import Child Components
import PlayerInfo from '../components/PlayerInfo';
import StatsBar from '../components/StatsBar';
import WordCloud from '../components/WordCloud';
import ClassificationList from '../components/ClassificationList';
import YouTubePlayer from '../components/YouTubePlayer'; // Ensure this component is updated
// Add these imports if they aren't already there
import { XMarkIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/solid';
// Import API Functions
import { fetchSnippetsByPlayer, fetchPlayerData } from '../api/snippets';
// Import other necessary functions if word cloud handlers are external
// import { handleAddFilterWord, handleRemoveFilterWord } from '../services/api';
import PlayerSummaryDisplay from '../components/PlayerSummaryDisplay'; // Import the new component

// Define the mobile breakpoint (Tailwind's md is 768px)
const MOBILE_BREAKPOINT = 768;
// Define header height for sticky positioning
const HEADER_HEIGHT_CLASS = 'top-16'; // Adjust if header height changes
// Define the width of the minimized player handle/tab visible on screen
const MINIMIZED_PLAYER_VISIBLE_WIDTH = '20px';

const PlayerPage = () => {
    const { playerid } = useParams();

    // --- State Variables ---
    const [playerData, setPlayerData] = useState(null);
    const [selectedSnippet, setSelectedSnippet] = useState(null);
    const [isMobile, setIsMobile] = useState(
        typeof window !== 'undefined' ? window.innerWidth < MOBILE_BREAKPOINT : false
    );
    const [showTopSection, setShowTopSection] = useState(
         typeof window !== 'undefined' ? window.innerWidth >= MOBILE_BREAKPOINT : false // Default collapsed on mobile
    );
    const [nameVisible, setNameVisible] = useState(false);
    const [isPlaying, setIsPlaying] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedDays, setSelectedDays] = useState(7);
    const [includeLowValue, setIncludeLowValue] = useState(false);
    const [isSourceModalOpen, setIsSourceModalOpen] = useState(false);
    const [availableSources, setAvailableSources] = useState([]);
    const [selectedSources, setSelectedSources] = useState(new Set());
    const [selectedFilterWords, setSelectedFilterWords] = useState([]);
    const [isPlayerMinimized, setIsPlayerMinimized] = useState(false);
    const [currentMobileSlideIndex, setCurrentMobileSlideIndex] = useState(0);

    // Refs and State for Animation
    const topSectionRef = useRef(null); // Ref for the *content* inside the collapsible div
    const [topHeight, setTopHeight] = useState('0px'); // Start collapsed or calculate initial height

    // --- Date Filter Options ---
    const dateOptions = [
        { label: '1D', value: 1 }, { label: '3D', value: 3 }, { label: '7D', value: 7 },
        { label: '30D', value: 30 }, { label: 'All', value: 0 },
    ];

    // --- Handlers ---
    const handlePause = () => setIsPlaying(false);
    const handleResume = () => selectedSnippet && setIsPlaying(true);
    const handlePlaySnippet = (snippet) => {
        setSelectedSnippet(snippet); setIsPlaying(true);
        if (isMobile) { setIsPlayerMinimized(false); setTimeout(() => { document.getElementById('mobile-sticky-player')?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }, 100); }
    };
    const handleStop = () => { setSelectedSnippet(null); setIsPlaying(false); setIsPlayerMinimized(false); };
    const togglePlayerMinimize = () => { setIsPlayerMinimized(prev => !prev); };

    const resetSelectionAndMinimize = useCallback(() => {
        setSelectedSnippet(null);
        setIsPlaying(false);
        setIsPlayerMinimized(false);
    }, [setSelectedSnippet, setIsPlaying, setIsPlayerMinimized]); // Dependencies are stable

    const handleDaysButtonClick = (value) => { if (!isLoading && value !== selectedDays) { setSelectedDays(value); setSelectedFilterWords([]); resetSelectionAndMinimize(); } };
    const handleIncludeLowValueChange = (event) => { setIncludeLowValue(event.target.checked); };
    const openSourceModal = () => setIsSourceModalOpen(true);
    const closeSourceModal = () => setIsSourceModalOpen(false);
    const handleSourceSelectionChange = (sourceName, isChecked) => { setSelectedSources(prev => { const next = new Set(prev); if (isChecked) next.add(sourceName); else next.delete(sourceName); setSelectedFilterWords([]); resetSelectionAndMinimize(); return next; }); };
    const handleSelectAllSources = () => { const newSelection = new Set(availableSources); if (newSelection.size !== selectedSources.size) { setSelectedSources(newSelection); setSelectedFilterWords([]); resetSelectionAndMinimize(); } };
    const handleSelectNoneSources = () => { if (selectedSources.size > 0) { setSelectedSources(new Set()); setSelectedFilterWords([]); resetSelectionAndMinimize(); } };

    const handleAddFilterWord = useCallback((word) => {
        const lowerCaseWord = typeof word === 'string' ? word.trim().toLowerCase() : '';
        if (lowerCaseWord) {
            setSelectedFilterWords(prevFilterWords => {
                if (!prevFilterWords.includes(lowerCaseWord)) {
                    resetSelectionAndMinimize(); // Call memoized version
                    return [...prevFilterWords, lowerCaseWord];
                }
                return prevFilterWords; // Word already exists, no change
            });
        }
    }, [setSelectedFilterWords, resetSelectionAndMinimize]); // Dependencies are stable

    const handleRemoveFilterWord = (wordToRemove) => { setSelectedFilterWords(prev => prev.filter(word => word !== wordToRemove)); resetSelectionAndMinimize(); };
    // Carousel Handlers
    const handleNextSlide = () => { setCurrentMobileSlideIndex(prev => Math.min(prev + 1, 3)); }; // Updated for 4 slides (0, 1, 2, 3)
    const handlePrevSlide = () => { setCurrentMobileSlideIndex(prev => Math.max(prev - 1, 0)); };
    // *** NEW: Touch Handlers for Carousel ***
    const MIN_SWIPE_DISTANCE = 50; // Minimum distance in pixels for a swipe

    const handleTouchStart = (e) => {
        // Allow default vertical scroll behavior but capture horizontal start
        if (e.touches.length !== 1) {
            setIsSwiping(false); // Cancel if multi-touch
            return;
        }
        setTouchEndX(null); // Reset end position
        setTouchStartX(e.touches[0].clientX);
        setIsSwiping(true); // Mark potential swipe start
    };

    const handleTouchMove = (e) => {
        if (touchStartX === null || !isSwiping || e.touches.length !== 1) {
            return;
        }
        setTouchEndX(e.touches[0].clientX);
        // Optional: Prevent vertical scroll WHILE swiping horizontally?
        // const distanceX = Math.abs(touchStartX - e.touches[0].clientX);
        // const distanceY = Math.abs(touchStartY - e.touches[0].clientY); // Need touchStartY state too
        // if (distanceX > distanceY) { e.preventDefault(); } // Only prevent if horizontal movement is dominant
    };

    const handleTouchEnd = () => {
        if (!touchStartX || !touchEndX || !isSwiping) {
            setIsSwiping(false);
            return;
        }
        const distance = touchStartX - touchEndX;
        const isLeftSwipe = distance > MIN_SWIPE_DISTANCE;
        const isRightSwipe = distance < -MIN_SWIPE_DISTANCE;

        if (isLeftSwipe) {
            handleNextSlide(); // Call existing handler
        } else if (isRightSwipe) {
            handlePrevSlide(); // Call existing handler
        }

        // Reset after swipe action is determined or distance is too small
        setTouchStartX(null);
        setTouchEndX(null);
        setIsSwiping(false);
    };
    // *** NEW: State/Refs for Swipe Handling ***
    const [touchStartX, setTouchStartX] = useState(null);
    const [touchEndX, setTouchEndX] = useState(null);
    const [isSwiping, setIsSwiping] = useState(false); // Optional: prevent clicks during swipe
    const carouselRef = useRef(null); // Ref for the carousel container

    // --- Effects ---

    // Fetch Player Details and Snippets
    useEffect(() => {
        const fetchData = async () => {
            setPlayerData(null); resetSelectionAndMinimize(); setIsLoading(true); setError(null); setSelectedFilterWords([]);
            try {
                if (!playerid) { throw new Error("No Player ID provided."); }
                const [playerResult, snippetsResult] = await Promise.allSettled([ fetchPlayerData(playerid), fetchSnippetsByPlayer(playerid, selectedDays === 0 ? null : selectedDays) ]);
                let player = null; let snippets = []; let playerError = null; let snippetError = null;
                if (playerResult.status === 'fulfilled' && playerResult.value?.length > 0) { player = playerResult.value[0]; } else { playerError = playerResult.reason?.message || 'Failed to load player details.'; console.error('Player Fetch Error:', playerResult.reason); }
                if (snippetsResult.status === 'fulfilled') { snippets = snippetsResult.value || []; } else { snippetError = snippetsResult.reason?.message || 'Failed to load snippets.'; console.error('Snippet Fetch Error:', snippetsResult.reason); }
                if (playerError) { setError(playerError); setPlayerData(null); } else { setPlayerData({ player, snippets: Array.isArray(snippets) ? snippets : [] }); if (snippetError) { console.warn("Snippet loading issue:", snippetError); } }
            } catch (error) { console.error('Error setting up player page fetch:', error); setError('An unexpected error occurred.'); setPlayerData(null); } finally { setIsLoading(false); }
        };
        fetchData();
    }, [playerid, selectedDays]);

    // *** UPDATED: Calculate Height for Collapsible Section ***
    useEffect(() => {
        let frameId = null;
        // Check if the section should be shown and the content ref is available
        if (topSectionRef.current && showTopSection && playerData?.player) {
            // Use requestAnimationFrame for desktop to ensure layout is calculated
            // For mobile, calculate immediately as 'auto' doesn't transition well
            if (!isMobile) {
                 frameId = requestAnimationFrame(() => {
                     if (topSectionRef.current) {
                         setTopHeight(`${topSectionRef.current.scrollHeight}px`);
                     }
                 });
            } else {
                 // Calculate height directly for mobile transition
                 setTopHeight(`${topSectionRef.current.scrollHeight}px`);
            }
        } else if (!showTopSection) {
            // Set height to 0 when collapsed
            setTopHeight('0px');
        }
        // Cleanup function for requestAnimationFrame
        return () => { if (frameId) { cancelAnimationFrame(frameId); } };
        // Dependencies that affect height or visibility
    }, [playerData, showTopSection, isMobile, currentMobileSlideIndex]); // Added currentMobileSlideIndex dependency

    // Control Name Visibility Animation (When Collapsed)
    useEffect(() => {
         let timer;
         if (!showTopSection) {
             timer = setTimeout(() => setNameVisible(true), 450);
         } else {
             setNameVisible(false);
         }
         return () => clearTimeout(timer);
    }, [showTopSection]);

    // Listen for window resize and set mobile state
    // Listen for window resize and set mobile state
    useEffect(() => {
        if (typeof window === 'undefined') return;

        const resizeCallback = () => { // Renamed for clarity: this is the function for the event listener
            const newIsMobile = window.innerWidth < MOBILE_BREAKPOINT;

            // Only update showTopSection and currentMobileSlideIndex if the mobile status *actually changed*
            if (newIsMobile !== isMobile) {
                setShowTopSection(!newIsMobile); // Set default visibility for the new mode
                if (newIsMobile) {
                    setCurrentMobileSlideIndex(0); // Reset mobile carousel only on transition to mobile
                }
            }

            // Logic to stop player only when transitioning from mobile to desktop
            // 'isMobile' (from closure) is the previous mobile state.
            if (isMobile && !newIsMobile && selectedSnippet) {
                handleStop();
            }

            // Always update the isMobile state itself to reflect the current viewport
            setIsMobile(newIsMobile);
        };

        window.addEventListener('resize', resizeCallback);

        // Initial setup for isMobile state on component mount.
        // The initial state of showTopSection is already correctly set by its own useState hook
        // based on the initial window width, so we don't need to call setShowTopSection here
        // unless isMobile's initial state was inaccurate (e.g. SSR vs client).
        // This ensures isMobile is correct from the start.
        const initialIsMobile = window.innerWidth < MOBILE_BREAKPOINT;
        if (isMobile !== initialIsMobile) {
            setIsMobile(initialIsMobile);
            // If initial isMobile was wrong, also set the correct showTopSection default
            // This case is less likely if useState for isMobile also checks window.innerWidth
            // but added for robustness.
             setShowTopSection(!initialIsMobile);
             if(initialIsMobile) {
                setCurrentMobileSlideIndex(0);
             }
        }


        return () => window.removeEventListener('resize', resizeCallback);
        // Dependencies:
        // isMobile: To compare previous vs new mobile state.
        // selectedSnippet & handleStop: For the M->D transition logic.
        // State setters: React guarantees these are stable.
    }, [isMobile, selectedSnippet, handleStop, setIsMobile, setShowTopSection, setCurrentMobileSlideIndex]);


     // Update Available & Selected Sources
     useEffect(() => { const allSnippets = playerData?.snippets || []; const uniqueSources = [...new Set( allSnippets.map(s => s?.video?.source?.name?.trim()).filter(name => name) )].sort(); setAvailableSources(uniqueSources); setSelectedSources(new Set(uniqueSources)); }, [playerData?.snippets]);

    // Filter Snippets Stage 1: Value and Source
    const filteredSnippetsStage1 = useMemo(() => { const allSnippets = playerData?.snippets || []; return allSnippets.filter(s => { const value = s.value?.trim().toLowerCase(); const keepValue = includeLowValue || value !== 'low'; const sourceName = s?.video?.source?.name?.trim(); const keepSource = selectedSources.size === 0 || (sourceName && selectedSources.has(sourceName)); return keepValue && keepSource; }); }, [playerData?.snippets, includeLowValue, selectedSources]);

    // Calculation: Snippet Stats
    const snippetStats = useMemo(() => { const snippetsToProcess = filteredSnippetsStage1; const total = snippetsToProcess.length || 0; if (total === 0) return { total: 0, positive: 0, negative: 0, netScorePercent: 0 }; let positiveCount = 0; let negativeCount = 0; snippetsToProcess.forEach(snippet => { const sentiment = snippet.sentiment?.trim().toLowerCase(); if (sentiment === 'positive') positiveCount++; else if (sentiment === 'negative') negativeCount++; }); const positivePercent = total > 0 ? (positiveCount / total) * 100 : 0; const negativePercent = total > 0 ? (negativeCount / total) * 100 : 0; const netScorePercent = Math.round(positivePercent - negativePercent); return { total, positive: positiveCount, negative: negativeCount, netScorePercent }; }, [filteredSnippetsStage1]);

    // Prepare snippets for child components
    const snippetsForTopComponents = filteredSnippetsStage1;
    const snippetsForClassificationList = filteredSnippetsStage1;

    // --- Render Logic ---

    if (isLoading && !playerData) { return <div className="flex justify-center items-center min-h-[calc(100vh-100px)]"><span className="loading loading-lg loading-spinner text-primary"></span><p className="ml-4">Loading Player Data...</p></div>; }
    if (error && !playerData?.player) { return <div role="alert" className="alert alert-error m-4 shadow-lg max-w-md mx-auto"><svg xmlns="http://www.w3.org/2000/svg" className="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg><span>Error! {error}</span></div>; }
    if (!playerid) return <div className="p-6 text-center font-semibold">No Player ID provided.</div>;
    if (!playerData || !playerData.player) return <div className="p-6 text-center font-semibold">Player data could not be loaded or found.</div>;

    const canRenderContent = !!playerData.player;

    return (
        <div className={`p-4 bg-base-200 text-base-content min-h-screen`}>

            {/* --- Top Bar (Filters) --- */}
             <div className="flex justify-between items-center mb-4 gap-x-2 sm:gap-x-4">
                 <div className="flex items-center gap-x-2 sm:gap-x-4 gap-y-2 flex-wrap">
                     <div className="flex items-center flex-shrink-0"><span className="mr-1 text-xs font-medium text-base-content/80 hidden sm:inline">Time:</span><div className="btn-group">{dateOptions.map(option => ( <button key={option.value} className={`btn btn-xs ${selectedDays === option.value ? 'btn-active btn-primary' : 'btn-ghost'}`} onClick={() => handleDaysButtonClick(option.value)} disabled={isLoading}>{option.label}</button>))}</div></div>
                     <button className="btn btn-primary btn-xs flex-shrink-0" onClick={openSourceModal} disabled={isLoading || availableSources.length === 0} title="Filter by Source"> Sources ({selectedSources.size}/{availableSources.length}) </button>
                     <div className="form-control flex-shrink-0"><label className="label cursor-pointer py-0 px-1 gap-2"><span className="label-text text-xs text-base-content/80"><span className="sm:hidden" title="Include Low Value Snippets">LV?</span><span className="hidden sm:inline">Include Low?</span></span><input type="checkbox" className="toggle toggle-primary toggle-xs" checked={includeLowValue} onChange={handleIncludeLowValueChange} disabled={isLoading}/></label></div>
                 </div>
                 {/* Collapse Button - Always Visible */}
                 <button onClick={() => setShowTopSection((prev) => !prev)} className="btn btn-ghost btn-sm p-1 flex-shrink-0" aria-label={showTopSection ? "Collapse" : "Expand"}>{showTopSection ? <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5"><path strokeLinecap="round" strokeLinejoin="round" d="m4.5 15.75 7.5-7.5 7.5 7.5" /></svg> : <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5"><path strokeLinecap="round" strokeLinejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" /></svg>}</button>
             </div>

            {/* --- Collapsible/Carousel Top Section --- */}
            <div
                // Apply transition and overflow hidden for smooth collapse/expand
                className={`transition-[max-height] ease-in-out duration-500 overflow-hidden mb-4 ${isLoading ? 'opacity-60' : ''}`}
                // Set max-height based on state and calculated height
                style={{ maxHeight: showTopSection ? topHeight : '0px' }}
            >
                {/* Inner div holds content and is measured by ref */}
                <div ref={topSectionRef}>
                    {canRenderContent && (
                        <>
                            {/* Conditionally render Desktop Row OR Mobile Carousel */}
                            {isMobile ? (
                                    // --- CORRECTED Mobile Carousel ---
                                    <div ref={carouselRef}
                                        className="relative overflow-hidden bg-base-100 rounded-lg shadow p-2 touch-pan-y" // touch-pan-y allows vertical scroll
                                        onTouchStart={handleTouchStart}
                                        onTouchMove={handleTouchMove}
                                        onTouchEnd={handleTouchEnd}
                                    >
                                        {/* Single Sliding Track */}
                                        <div
                                            className="flex transition-transform duration-300 ease-in-out"
                                            style={{ transform: `translateX(-${currentMobileSlideIndex * 100}%)` }}
                                        >
                                            {/* Slide 1: PlayerInfo */}
                                            <div className="w-full flex-shrink-0 px-1">
                                                <PlayerInfo player={playerData.player} totalSnippets={snippetStats.total} netSentimentScorePercent={snippetStats.netScorePercent} />
                                            </div>
                                            {/* Slide 2: StatsBar */}
                                            <div className="w-full flex-shrink-0 px-1">
                                                <div className="h-full flex flex-col justify-center">
                                                    <StatsBar snippets={snippetsForTopComponents} />
                                                </div>
                                            </div>
                                            {/* Slide 3: PlayerSummaryDisplay */}
                                            <div className="w-full flex-shrink-0 px-1">
                                                <div className="min-h-[200px]"> {/* Ensure component has some minimum height */}
                                                    <PlayerSummaryDisplay summaryData={playerData.player?.summary} isMobile={isMobile} />
                                                </div>
                                            </div>
                                            {/* Slide 4: WordCloud */}
                                            <div className="w-full flex-shrink-0 px-1">
                                                <div className="min-h-[200px]"> {/* Ensure WordCloud has some minimum height */}
                                                    <WordCloud snippets={snippetsForTopComponents} player={playerData.player} onWordSelect={handleAddFilterWord} />
                                                </div>
                                            </div>
                                        </div>

                                        {/* Navigation Chevrons - Siblings to the sliding track, children of carouselRef */}
                                        {currentMobileSlideIndex > 0 && (
                                            <button
                                                onClick={handlePrevSlide}
                                                className="absolute top-1/2 left-1 sm:left-2 transform -translate-y-1/2 btn btn-circle btn-ghost btn-sm text-white bg-black/40 hover:bg-black/60 z-20 focus:outline-none focus:ring-2 focus:ring-primary"
                                                aria-label="Previous Slide"
                                                title="Previous"
                                            >
                                                <ChevronLeftIcon className="w-5 h-5 sm:w-6 sm:h-6" />
                                            </button>
                                        )}
                                        {currentMobileSlideIndex < 3 && ( // Assuming 4 slides (indices 0, 1, 2, 3)
                                            <button
                                                onClick={handleNextSlide}
                                                className="absolute top-1/2 right-1 sm:right-2 transform -translate-y-1/2 btn btn-circle btn-ghost btn-sm text-white bg-black/40 hover:bg-black/60 z-20 focus:outline-none focus:ring-2 focus:ring-primary"
                                                aria-label="Next Slide"
                                                title="Next"
                                            >
                                                <ChevronRightIcon className="w-5 h-5 sm:w-6 sm:h-6" />
                                            </button>
                                        )}

                                        {/* Navigation Dots - Siblings to the sliding track, children of carouselRef */}
                                        <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 flex space-x-1.5 z-20">
                                            {[0, 1, 2, 3].map(index => ( // Assuming 4 slides
                                                <button
                                                    key={index}
                                                    onClick={() => setCurrentMobileSlideIndex(index)}
                                                    className={`w-2.5 h-2.5 rounded-full transition-colors duration-200 ${currentMobileSlideIndex === index ? 'bg-primary scale-110' : 'bg-base-content/40 hover:bg-base-content/60'}`}
                                                    aria-label={`Go to slide ${index + 1}`}
                                                    title={`Go to slide ${index + 1}`}
                                                ></button>
                                            ))}
                                        </div>
                                    </div>
                            ) : (
                                // --- Desktop Row Layout ---
                                <div className="flex flex-row gap-4">
                                    <div className="w-auto basis-auto flex-shrink-0"><PlayerInfo player={playerData.player} totalSnippets={snippetStats.total} netSentimentScorePercent={snippetStats.netScorePercent} /></div>
                                    <div className="w-auto basis-1/4 flex flex-col"><StatsBar snippets={snippetsForTopComponents} /></div>
                                    {/* Added PlayerSummaryDisplay for desktop - it takes up basis-1/4 similar to StatsBar or adjust as needed */}
                                    <div className="w-auto basis-1/4 flex flex-col min-w-0"> {/* Added min-w-0 for flexBasis to work properly with potentially long content */} 
                                        <div className="flex-grow bg-base-100 p-1 rounded-lg shadow overflow-y-auto min-h-[200px]"> {/* Adjusted padding to p-1 to match summary display style if needed */} 
                                            <PlayerSummaryDisplay summaryData={playerData.player?.summary} isMobile={isMobile} />
                                        </div>
                                    </div>
                                    <div className="flex-grow min-w-0 flex flex-col"><div className="flex-grow bg-base-100 p-2 rounded-lg shadow overflow-y-auto min-h-[200px]"><WordCloud snippets={snippetsForTopComponents} player={playerData.player} onWordSelect={handleAddFilterWord} /></div></div>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
            {/* --- End Collapsible/Carousel Top Section --- */}


             {/* --- Player Name display when collapsed (Works for Mobile and Desktop) --- */}
             {canRenderContent && (
                <div className={`transition-opacity duration-500 text-center mb-4 ${!showTopSection && nameVisible ? 'opacity-100' : 'opacity-0'} ${showTopSection ? 'h-0 invisible' : 'h-auto visible'}`}>
                    <h1 className="text-xl sm:text-2xl font-semibold truncate px-2">
                         {playerData.player.name} | {playerData.player.team} - {playerData.player.position}
                    </h1>
                 </div>
             )}

             {/* --- Sticky Player Container for Mobile --- */}
             {isMobile && selectedSnippet && (
                 <div
                    id="mobile-sticky-player-container" // Use this ID for scrolling if needed
                    // Fixed position below header, use flex for layout
                    // Added items-stretch to make player and controls fill height
                    className={`fixed md:hidden ${HEADER_HEIGHT_CLASS} left-0 z-30 w-full h-auto
                                bg-gradient-to-b from-base-300 via-base-200 to-base-200/90
                                shadow-lg transition-transform duration-300 ease-in-out transform flex items-stretch
                        ${isPlayerMinimized ? `-translate-x-[calc(100%-44px)]` : 'translate-x-0'}
                    `} // Adjust visible width (e.g., 44px for controls)
                 >
                    {/* Player takes most space */}
                    <div className="flex-grow min-w-0 overflow-hidden rounded-l-lg"> {/* Added overflow-hidden and rounding */}
                         <YouTubePlayer
                             selectedSnippet={selectedSnippet}
                             isPlaying={isPlaying}
                             isMobile={true}
                             isMinimized={isPlayerMinimized}
                             // Removed button props: onClose, onToggleMinimize
                         />
                    </div>
                    {/* Controls container (fixed width, vertical flex) */}
                    <div className={`flex-shrink-0 w-[44px] flex flex-col items-center justify-center gap-y-3 px-1 bg-inherit rounded-r-lg shadow-inner ${isPlayerMinimized ? 'opacity-100' : 'opacity-90 hover:opacity-100'}`}> {/* Adjust width to match transform */}
                        {/* Minimize/Expand Button */}
                        <button
                            onClick={togglePlayerMinimize}
                            className="p-1.5 bg-black/40 text-white rounded-full hover:bg-black/60 focus:outline-none focus:ring-1 focus:ring-white"
                            aria-label={isPlayerMinimized ? "Expand Player" : "Minimize Player"}
                            title={isPlayerMinimized ? "Expand Player" : "Minimize Player"}
                        >
                            {isPlayerMinimized ? (
                                <ChevronRightIcon className="w-5 h-5" />
                            ) : (
                                <ChevronLeftIcon className="w-5 h-5" />
                            )}
                        </button>
                        {/* Close Button - Show only when Expanded */}
                        {!isPlayerMinimized && (
                             <button
                                onClick={handleStop} // Use handleStop to close fully
                                className="p-1.5 bg-black/40 text-white rounded-full hover:bg-black/60 focus:outline-none focus:ring-1 focus:ring-white"
                                aria-label="Close Player"
                                title="Close Player"
                            >
                                <XMarkIcon className="w-5 h-5" />
                            </button>
                        )}
                    </div>
                 </div>
             )}
             {/* --- End Sticky Player Container --- */}

             {/* --- Bottom Section (Main Content Grid) --- */}
             <div className="mt-4">
                {canRenderContent && (
                    <div className="flex flex-col md:flex-row gap-4 items-start">
                        {/* YouTube Player Area - DESKTOP ONLY */}
                        {!isMobile && (
                            <div className="w-full md:w-2/3 sticky top-16 z-10 order-first md:order-last">
                                {/* Show player and its custom border/title only when a snippet is selected */}
                                {selectedSnippet && (
                                    <div className="bg-black rounded-lg shadow-xl overflow-hidden"> {/* Main black frame */}
                                        {/* Optional: If your YouTubePlayer component doesn't maintain aspect ratio, you can wrap it */}
                                        <div className="aspect-video bg-neutral-800"> {/* Fallback bg for player area */}
                                            <YouTubePlayer
                                                selectedSnippet={selectedSnippet}
                                                isPlaying={isPlaying}
                                                isMobile={false}
                                                // Ensure your YouTubePlayer component is styled to be responsive (e.g., width: 100%, height: 100%)
                                            />
                                        </div>
                                        <div className="text-white text-sm px-3 py-2 truncate"> {/* Title bar area */}
                                            {selectedSnippet.video?.title || selectedSnippet.title || selectedSnippet.name || "Video"}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

            {/* Classification List */}
            <div className={`w-full md:w-1/3 bg-base-300 p-4 rounded-xl shadow-md order-last md:order-first ${selectedSnippet && !isMobile ? 'min-h-[calc(100vh-8rem)]' : ''}`}> {/* Optional: ensure this column can be long enough for scrolling */}
                <ClassificationList
                    allSnippets={snippetsForClassificationList}
                    selectedFilterWords={selectedFilterWords}
                    onRemoveFilterWord={handleRemoveFilterWord}
                    onAddFilterWord={handleAddFilterWord}
                    onSnippetClick={handlePlaySnippet}
                    onPause={handlePause}
                    onResume={handleResume}
                    onStop={handleStop}
                    isPlaying={isPlaying}
                    currentSnippetId={selectedSnippet?.id}
                            />
                        </div>
                    </div>
                )}
            </div>

             {/* --- Source Filter Modal --- */}
             <dialog id="source_filter_modal" className={`modal ${isSourceModalOpen ? 'modal-open' : ''}`}>
                 <div className="modal-box"><h3 className="font-bold text-lg mb-4">Filter by Source</h3><div className="max-h-60 overflow-y-auto space-y-1 pr-2 mb-4">{availableSources.length > 0 ? (availableSources.map(sourceName => (<div className="form-control" key={sourceName}><label className="label cursor-pointer py-0.5 justify-start gap-2"><input type="checkbox" className="checkbox checkbox-primary checkbox-xs" checked={selectedSources.has(sourceName)} onChange={(e) => handleSourceSelectionChange(sourceName, e.target.checked)}/><span className="label-text text-xs">{sourceName}</span></label></div>))) : (<p className="text-center italic text-base-content/60">No sources found.</p>)}</div><div className="modal-action justify-between mt-4"><div className="flex gap-2"><button className="btn btn-ghost btn-sm" onClick={handleSelectAllSources} disabled={availableSources.length === 0}>All</button><button className="btn btn-ghost btn-sm" onClick={handleSelectNoneSources} disabled={availableSources.length === 0}>None</button></div><button className="btn btn-primary btn-sm" onClick={closeSourceModal}>Close</button></div></div><form method="dialog" className="modal-backdrop"><button onClick={closeSourceModal}>close</button></form>
             </dialog>

        </div> // End Main Page Div
    );
};

export default PlayerPage;
